import { notifications } from '@mantine/notifications'
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { getAutoGenerationConfigs } from '@/configs/aiFieldConfigs'
import type { AIFieldState } from '@/hooks/types/aiFieldGeneration'
import type { useAIFieldGeneration } from '@/hooks/useAIFieldGeneration'
import { useGlobalState } from '@/hooks/useGlobalState'
import type { GetContentModuleResponse, UpdateContentModuleParams } from '@/services/api/api.types'
import * as commonClasses from '@/styles/componentCommons.css'
import { splitMediaSteps } from '@/utils/contentModuleForm'
import { isModuleFieldGenerated, type ProvenanceModuleField } from '@/utils/fieldProvenance'
import { getStepStatus, type StepStatus } from '@/utils/stepReadiness'

import type { CMFormSection } from './ContentModuleFormScreen'

type AIGeneration = ReturnType<typeof useAIFieldGeneration>

/**
 * Which module fields a pass started by the primary reaching Ready to Submit, or by the
 * primary being changed, may write — decisions 37, 41 and 46.
 *
 * Untouched means the field still holds the machine's text, or holds nothing at all.
 * Anything else is the expert's and is left exactly as it is.
 *
 * Provenance describes what is *stored*, so it only classifies a field the form still
 * agrees with. An unsaved divergence is something the marker cannot have seen, and the
 * safe reading of that is the expert's.
 */
export function selectFieldsToGenerate(
  cm: Partial<GetContentModuleResponse> | undefined,
  values: Record<string, unknown>,
): string[] {
  return getAutoGenerationConfigs('cm')
    .map(config => config.formField)
    .filter(field => {
      const value = values[field]
      if (typeof value === 'string' && value.trim()) {
        const saved = cm?.[field as ProvenanceModuleField]
        if (typeof saved !== 'string' || saved.trim() !== value.trim()) return false
        return isModuleFieldGenerated(cm, field as ProvenanceModuleField)
      }
      return true
    })
}

/**
 * The stricter filter, for the catch-up on open — decision 47.
 *
 * Blanks only. The expert is arriving at a module rather than watching one finish, so the
 * question is "did this module never get its fields", not "are these still refreshable".
 * Running `selectFieldsToGenerate` here instead would rewrite every untouched module on
 * every visit, since a field nobody has edited is by definition still the machine's.
 */
export function selectEmptyFields(values: Record<string, unknown>): string[] {
  return getAutoGenerationConfigs('cm')
    .map(config => config.formField)
    .filter(field => {
      const value = values[field]
      return typeof value !== 'string' || !value.trim()
    })
}

/**
 * What each form field writes on the module. `name` is stored twice, exactly as the full
 * save does (`castZodFormToContentModule`) — writing one without the other leaves the
 * module's two titles disagreeing.
 */
const SAVE_FIELDS: Record<string, (value: string) => Partial<UpdateContentModuleParams>> = {
  name: value => ({ name: value, title: value }),
  shortDescription: value => ({ short_description: value }),
  introduction: value => ({ introduction: value }),
}

/**
 * The saved fields alone, for moving the form's dirty baseline onto them without moving it
 * for anything else. Named fields only, and only where the form actually has one.
 */
export function pick(values: Record<string, unknown>, fields: string[]): Record<string, unknown> {
  // `!== undefined` rather than `in`: a key the form carries but holds `undefined` for would
  // otherwise be spread over `getInitialValues()` and move that field's baseline to
  // `undefined`, which is the very thing this exists to avoid. The form does carry such keys
  // — `priority` is written `?? undefined` by the cast.
  return Object.fromEntries(fields.filter(field => values[field] !== undefined).map(field => [field, values[field]]))
}

/**
 * The fields a pass may actually write. Anything blank is dropped: a generation that came
 * back empty has nothing to save, and sending `''` would clear the module's field.
 *
 * Separate from `buildSavePayload` so the dirty baseline can be moved for exactly the fields
 * the request carried. Deriving the two from one list looked equivalent and was not — a
 * field dropped here still had its baseline rebased, so a value the server never received
 * stopped counting as unsaved and the leave-guard went quiet on it.
 */
export function savableFields(fields: string[], values: Record<string, unknown>): string[] {
  return fields.filter(field => {
    const value = values[field]
    return !!SAVE_FIELDS[field] && typeof value === 'string' && !!value.trim()
  })
}

export function buildSavePayload(
  fields: string[],
  values: Record<string, unknown>,
): Partial<UpdateContentModuleParams> {
  return savableFields(fields, values).reduce<Partial<UpdateContentModuleParams>>(
    (payload, field) => ({ ...payload, ...SAVE_FIELDS[field]!(values[field] as string) }),
    {},
  )
}

/**
 * Which of a pass's fields actually landed, and so may be saved.
 *
 * `idle` is where success, failure and cancellation all come to rest, so the outcome is the
 * only thing that tells them apart — and a cancelled field has none. This is deliberately a
 * whitelist of `succeeded` rather than "not failed": the field the expert cancelled by
 * typing over it still holds their text, and the looser test would write that to the module
 * as though the Sidekick had produced it. The same mistake had to be fixed once already in
 * the pass this replaces.
 */
export function succeededFields(fields: string[], outcomeOf: (field: string) => AIFieldState['lastOutcome']): string[] {
  return fields.filter(field => outcomeOf(field) === 'succeeded')
}

/** Which rule the pass about to run should use. */
export type GenerationMode = 'arrival' | 'transition'

/** What is known about the primary currently in the slot. */
export type GenerationEpisode = { stepId: string; mode: GenerationMode; generated: boolean }

/**
 * The episode for the primary now in the slot, carried forward or started fresh.
 *
 * Held for the *current* primary only, not remembered per step id for ever. Two things
 * depend on that. A mode must not change under a pass that is waiting for the form to
 * initialize, so it is decided once while the step id holds — but a primary swapped away and
 * later swapped back is a new episode and must be re-decided, or decision 46's refresh never
 * happens on the way back and the module keeps fields written from the file that no longer
 * leads. `null` means nothing is known yet and nothing should be recorded.
 */
export function nextEpisode(
  previous: GenerationEpisode | null,
  stepId: string,
  status: StepStatus,
  seenAnyPrimary: boolean,
): GenerationEpisode | null {
  if (previous?.stepId === stepId) return previous

  const mode = classifyPrimary(status, seenAnyPrimary)
  return mode ? { stepId, mode, generated: false } : null
}

/**
 * How to treat a primary the first time it is seen in a *known* state. `null` means
 * nothing is known about it yet and nothing should be recorded.
 *
 * A settling primary never reaches here — the caller returns first, for the same reason the
 * `checking` guard exists.
 *
 * The `checking` guard is the whole reason this is a function rather than a comparison.
 * Statuses arrive in two waves: `/cm/steps_content` only runs once the steps list has
 * succeeded, so until its snapshot lands every step reads `statusPending` and resolves as
 * `checking`. **Every module therefore walks `checking` -> `ready` on open**, and a
 * trigger that reads that as a file becoming ready regenerates every module on every
 * visit. Treating `checking` as no observation at all is what tells the two apart.
 *
 * Past that: ready before any primary has been seen is the expert arriving at a module
 * (decision 47, blanks only). Anything else is a file we watched settle, or a primary that
 * replaced one already seen — both of which refresh what is still the machine's
 * (decisions 37 and 46).
 */
export function classifyPrimary(status: StepStatus, seenAnyPrimary: boolean): GenerationMode | null {
  if (status === 'checking') return null
  return status === 'ready' && !seenAnyPrimary ? 'arrival' : 'transition'
}

/**
 * Drops anything the expert has actually changed this session.
 *
 * Both filters read *values*, and a field the expert has just emptied looks exactly like a
 * field that arrived empty. Without this, clearing the Title to write your own is enough to
 * make the catch-up fill it back in — and in arrival mode it saves what it wrote, so on a
 * live module the Sidekick's text publishes over the expert mid-keystroke.
 *
 * Dirty, not touched. Mantine sets touched on **focus** (`getInputProps` adds an `onFocus`
 * under its default `withFocus`), and `RequiredSettingsSection` spreads those props straight
 * onto the input — so clicking into Title while the file is still processing, or tabbing
 * through the form, would mark it touched without changing anything and cancel its
 * first-ever generation for good: no spinner, no message, just a required field still blank
 * at save. Restoring a draft has the same shape from the other side, since `setTouched`
 * replaces the whole map. Dirty asks the question the docstring means — is this value the
 * expert's? — and a pass that saves rebases the fields it wrote, so passes still chain.
 */
export function uneditedFields(fields: string[], isEdited: (field: string) => boolean): string[] {
  return fields.filter(field => !isEdited(field))
}

/** The fields a pass may write, which is the only thing the two modes disagree about. */
export function fieldsForMode(
  mode: GenerationMode,
  cm: Partial<GetContentModuleResponse> | undefined,
  values: Record<string, unknown>,
): string[] {
  return mode === 'arrival' ? selectEmptyFields(values) : selectFieldsToGenerate(cm, values)
}

export type PrimaryFileGeneration = ReturnType<typeof usePrimaryFileGeneration>

/**
 * Generates the module's fields from the primary file — decisions 37, 46 and 47, which
 * replace the "Done Adding Files" button.
 *
 * Three edges, two rules. The primary reaching Ready to Submit and the primary being
 * changed or replaced both refresh anything still the machine's; arriving at a module
 * whose primary is already ready only fills blanks. Every pass persists what it wrote
 * (decision 48), so an expert who closes the tab keeps it.
 *
 * The mode is decided once per primary, when its first *known* status is observed, and
 * not re-derived afterwards — a pass that has to wait for the form to initialize must not
 * come back as a different kind of pass.
 */
export function usePrimaryFileGeneration(props: CMFormSection, ai: AIGeneration) {
  const { api } = useGlobalState()
  const { markModuleSaved, isDraftPending } = props
  const [inFlight, setInFlight] = useState<string[] | null>(null)
  // The write counts as part of the pass. `ActionsSection` holds Save while this is true so a
  // click cannot navigate away mid-pass and abort it; releasing at the last field's *outcome*
  // let Save back in while the save itself was still in the air, where a second full-form
  // update races the partial one and the winner is whichever lands last.
  const [saving, setSaving] = useState(false)
  const running = !!inFlight || saving

  const steps = props.stepsQuery.data
  // Resolved the way every other consumer resolves it, dangling-id fallback included, so
  // this cannot read a different file from the one sitting in the Primary slot.
  const primary = useMemo(
    () => splitMediaSteps(steps, props.cmQuery.data?.primaryStepId).primary,
    [steps, props.cmQuery.data?.primaryStepId],
  )
  const status = primary ? getStepStatus(primary) : undefined

  const episodeRef = useRef<GenerationEpisode | null>(null)
  const observedRef = useRef(false)
  // What the machine wrote into each of the current pass's fields, recorded the moment it
  // landed. Membership says the touch flag has been cleared; the value says whether what is in
  // the field now is still the machine's, which is the only reliable way to ask — `isTouched`
  // is set by focus, so reading it here would drop a field the expert merely clicked into.
  const machineWrote = useRef<Map<string, string>>(new Map())

  // Every route element carries its own key, so one module's edit page and another's are the
  // same mounted component. No in-app path gets from one to the other without passing through
  // the list, which does unmount it, but nothing here should depend on that staying true.
  //
  // `inFlight` and `saving` go with the refs: a pass left running would settle against the
  // *new* module's form, and a save left marked in flight would hold the new module's Save
  // button on the old module's write.
  const currentTaskId = useRef(props.taskId)
  useLayoutEffect(() => {
    currentTaskId.current = props.taskId
  }, [props.taskId])
  const [stateTaskId, setStateTaskId] = useState(props.taskId)
  if (stateTaskId !== props.taskId) {
    setStateTaskId(props.taskId)
    setInFlight(null)
    setSaving(false)
  }
  useEffect(() => {
    episodeRef.current = null
    observedRef.current = false
    machineWrote.current.clear()
  }, [props.taskId])

  // `persist` continues after an await, and the expert can leave in that window — discarding
  // their changes on the way out, which clears the draft cache. Everything downstream of the
  // write would then run on a dead form and put the cache back, offering them a restore of
  // work they just chose to throw away.
  const mounted = useRef(true)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])

  /**
   * Writes the pass's results straight to the module — decision 48.
   *
   * Only the fields it generated, because `prepare_for_db` leaves an omitted field alone:
   * sending the whole form would persist whatever half-typed text is sitting in the other
   * inputs. And `lock: false`, because `/cm/update` clears `locked_by` under the default
   * and would hand the module to someone else while the expert is still in it — the same
   * reason the primary swap sends it.
   *
   * Goes to the API directly rather than through `cmUpdateMutation`, which belongs to the
   * Save button: its `onError` says "Could not save your changes, please try again", which
   * names an action the expert never took on a module they may not have typed into, and its
   * `isPending` would spin the Save button for a save they did not ask for. This reports its
   * own failure in its own terms instead.
   */
  const persist = useCallback(
    async (fields: string[]) => {
      const taskId = props.taskId
      if (!taskId) return

      const values = props.moduleForm.getValues() as Record<string, unknown>
      // `succeededFields` gates on the *outcome*, but the payload reads the live form and a
      // pass runs for tens of seconds — so an expert who starts rewriting the Title after it
      // lands would otherwise have their half-typed sentence POSTed, without pressing Save and
      // past the live-module confirmation the Save button requires. Compared by value against
      // what the machine wrote, not by `isTouched`: touched is set by focus, so clicking into
      // the new title to read it would silently drop it from its own save.
      const writable = fields.filter(field => {
        const wrote = machineWrote.current.get(field)
        return wrote === undefined || wrote === values[field]
      })
      const payload = buildSavePayload(writable, values)
      if (!Object.keys(payload).length) return

      try {
        await api.updateContentModule({ task_id: taskId, lock: false, ...payload })
      } catch (error) {
        // The fields are on screen and the expert can still press Save, so a failed write
        // must not take the values with it — and the message says that, rather than asking
        // them to retry something they never started.
        console.error('[usePrimaryFileGeneration] Failed to save generated fields:', error)

        // Said first and unconditionally: the write really did fail, and that is worth knowing
        // whether or not the expert is still looking at this module.
        notifications.show({
          title: 'Module Fields Not Saved',
          message: 'The Sidekick wrote your module fields but they could not be saved. Use Save Module to keep them.',
          color: 'red',
          classNames: commonClasses.notificationFilled,
          position: 'bottom-left',
        })

        // The rest writes to the form on screen *now*, so it is only correct while that is
        // still this module's — the same guard the success path uses.
        if (!mounted.current || currentTaskId.current !== taskId) return

        // Put the touch flags back. Each field was untouched as it landed, on the assumption
        // this write would make that true — the machine's own work is not the expert's unsaved
        // work. The write failed, so it is unsaved work now, and it has to count as such: the
        // leave guard and the draft cache both arm on dirty *and* touched, so leaving these
        // dirty-but-untouched means an expert who misses the notification navigates away with
        // no prompt and nothing cached, and the generated text is gone.
        props.moduleForm.setTouched(current => ({
          ...current,
          ...Object.fromEntries(writable.map(field => [field, true])),
        }))
        return
      }

      // The write landed, but the expert may have left while it was in the air, or moved to
      // another module. Nothing below is about the server: it all reads and rewrites the form
      // that is on screen *now*, so it is only correct while that is still this module's.
      if (!mounted.current || currentTaskId.current !== taskId) return

      // Everything past here runs only once the write has landed, so none of it can report a
      // save that succeeded as a failure. Scoped to what the payload actually carried:
      // `buildSavePayload` drops a field that generated nothing usable, and rebasing one the
      // server never received would stop it counting as unsaved while it still differs.
      // Only what is still the machine's.
      const saved = savableFields(writable, values)

      // These fields now match the server, so they must stop counting as unsaved — without
      // this, merely opening a module and letting the catch-up run leaves the leave-guard
      // warning about changes that were already written.
      //
      // Rebased off the *initial* values rather than the current ones, which is the whole
      // point: `resetDirty` adopts whatever is on screen as the new baseline, so a bare call
      // would quietly mark an edit the expert made while this was in flight as saved too.
      // Merging over `getInitialValues()` moves the baseline for these fields alone and
      // leaves every other field comparing against what it loaded with. `setDirty` is not the
      // tool here: any override at all switches `isDirty()` off value comparison for the
      // whole form.
      props.moduleForm.resetDirty({ ...props.moduleForm.getInitialValues(), ...pick(values, saved) })

      // The draft cache is a second, separate record of unsaved work, and it may already
      // hold a snapshot taken before this save landed. `markModuleSaved` drops that and
      // re-derives whether anything is still worth caching, which is why it is not a plain
      // delete: the expert may have their own edit in another field that still needs it.
      markModuleSaved?.()

      // Provenance and the saved values both move with this write, and both are read by the
      // next pass. Without the refetch a later swap compares the form against the values
      // this save replaced, reads the difference as the expert's, and regenerates nothing —
      // decision 46 would work once per module and then stop.
      await api.queryClient.invalidateQueries({ queryKey: ['cm', taskId] })
      // The generated title is what the Content Library lists this module under.
      api.queryClient.invalidateQueries({ queryKey: ['cm', 'list'] })
    },
    [api, props.taskId, props.moduleForm, markModuleSaved],
  )

  useEffect(() => {
    // Nothing is observed until the module itself has loaded. `splitMediaSteps` falls back
    // to the first step whenever `primaryStepId` is absent, which it is for the whole window
    // before `cmQuery` resolves — so on a revisit, where the cached status snapshot makes
    // the steps read `ready` immediately, that fallback step would be recorded as an arrival
    // and the real primary would then look like a swap into it. Opening a finished module
    // would regenerate the lot: the same failure the `checking` guard exists to stop, through
    // a different door.
    if (!primary || !status || !props.cmQuery.isSuccess) return
    // A settling file reads `analyzing` so its badge doesn't flash (SME-1126), but nothing
    // is known about it yet — recording that would classify opening a module whose primary
    // the snapshot omitted as a transition, which is the `checking` door again.
    if (primary.settling) return

    const episode = nextEpisode(episodeRef.current, primary.stepId, status, observedRef.current)
    if (!episode) return
    episodeRef.current = episode
    observedRef.current = true

    if (status !== 'ready' || episode.generated || running) return
    // The module form is uncontrolled and fills from `cmQuery` a render late. Before that
    // its values are the schema's blanks, so every field reads empty and every module would
    // look like it needs its fields written.
    if (!props.moduleForm.initialized) return
    // Both filters read the form, and while the restore prompt is open the form holds the
    // server's values rather than the expert's unsaved ones. Deciding from that would fill a
    // blank the draft is about to restore, then overwrite the restored text when it lands.
    if (isDraftPending?.()) return

    const fields = uneditedFields(
      fieldsForMode(episode.mode, props.cmQuery.data, props.moduleForm.getValues()),
      field => props.moduleForm.isDirty(field),
    )
    // Only a pass that actually runs closes the episode. Marking it done regardless burned
    // the primary for good, so a swap away and back found nothing left to do.
    if (!fields.length) return

    episode.generated = true
    machineWrote.current.clear()
    setInFlight(fields)
    fields.forEach(field => ai.generateFieldAndAccept('cm', field))
  }, [primary, status, running, props.moduleForm, props.cmQuery.data, props.cmQuery.isSuccess, isDraftPending, ai])

  /**
   * Keeps a pass's own writes out of the form's "the expert has unsaved work" record, from the
   * moment each one lands rather than only once the save returns.
   *
   * Auto-accept writes through `setFieldValue`, which marks the field touched, and the leave
   * guard and the draft cache both arm on dirty *and* touched. Between the first field landing
   * and the save returning — tens of seconds for three fields — an expert who navigates away is
   * otherwise asked to confirm discarding changes they never made, and confirming aborts the
   * requests still in flight, so the pass is thrown away with nothing saying so.
   *
   * Once per field, at the machine's write, and never again: a standing suppression would keep
   * clearing the flag afterwards, so an expert who types into one of these fields while the
   * pass is still running would leave no trace of unsaved work at all — no leave guard, no
   * cached draft — and lose what they wrote on the way out. Their keystroke cancels that
   * field's generation, so a cancelled field never reaches `succeeded` and is never untouched
   * here; one they edit after it lands is re-touched by that keystroke and stays that way.
   */
  useEffect(() => {
    if (!inFlight) return
    const landed = inFlight.filter(field => {
      if (machineWrote.current.has(field)) return false
      const state = ai.getFieldState(field, 'cm')
      // `idle` as well as the outcome. The outcome is set while the field is still `pending`,
      // before the accept writes the value — reading it alone is correct today only because
      // `useAIFieldGeneration` is called first and its auto-accept lands in the same commit.
      // Asking for the resting state puts the question directly rather than resting on that
      // order: were it to change, this would record the *previous* value as the machine's and
      // then drop the real one from its own save.
      return state.state === 'idle' && state.lastOutcome === 'succeeded'
    })
    if (!landed.length) return

    const current = props.moduleForm.getValues() as Record<string, unknown>
    landed.forEach(field => machineWrote.current.set(field, typeof current[field] === 'string' ? current[field] : ''))
    props.moduleForm.setTouched(current => {
      if (!landed.some(field => current[field])) return current
      const next = { ...current }
      landed.forEach(field => delete next[field])
      return next
    })
  })

  // `idle` is where success, failure and cancellation all come to rest, so what happened is
  // read from the outcome. Only what actually landed is saved.
  useEffect(() => {
    if (!inFlight) return
    if (!inFlight.every(field => ai.getFieldState(field, 'cm').state === 'idle')) return

    const written = succeededFields(inFlight, field => ai.getFieldState(field, 'cm').lastOutcome)
    // eslint-disable-next-line react-hooks/set-state-in-effect -- settles on `useAIFieldGeneration`'s field states and starts the save; clearing `inFlight` during render would end the pass before the untouch effect above records the last field to land
    setInFlight(null)
    if (!written.length) return

    setSaving(true)
    const savingFor = currentTaskId.current
    void persist(written).finally(() => {
      // Only release the button this pass is holding. A module switch already cleared `saving`
      // for the new module, and an old write resolving must not reach in and release it again
      // mid-save — the race this flag exists to stop.
      if (mounted.current && currentTaskId.current === savingFor) setSaving(false)
    })
  }, [inFlight, ai, persist])

  return useMemo(() => ({ running }), [running])
}
